import {
    mdiMenu,
    mdiWeb,
} from '@mdi/js';
import Icon from '@mdi/react';
import { Button, Stack, SxProps } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useBreakpoints from '../../../hooks/useBreakpoint';
import { vars } from '../../../Theme';
import Logo from '../Logo/Logo';
import HeaderMenuButton from './HeaderMenuButton';
import UserControl from './UserControl/UserControl';
import LanguageOption from './LanguageOption';
import i18n from '../../../localization/i18n';

const headerButtonSx: SxProps = {
    minWidth: '24px',
    justifyContent: 'left',
    textTransform: 'none',
    color: vars.font,
};

/**
 * Component for a button that is shown in the header.
 */
const HeaderButton = (props: {
    text?: string;
    title?: string;
    icon?: string;
    onClick?: React.MouseEventHandler;
    sx?: SxProps;
}) => {
    /** Deconstruct props. */
    const { icon, title, text, onClick, sx } = props;

    return (
        <Button title={title} onClick={onClick} sx={{ ...headerButtonSx, ...sx }}>
            {icon && <Icon path={icon} size={1} />}
            {text}
        </Button>
    );
};

/**
 * Main application header.
 */
export default () => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'header' });

    /** Determine screen size. */
    const { isDesktop, isTablet, isMobile } = useBreakpoints();

    /** State slice that controls the open state of the header's menu on mobile devices. */
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    /** Header element styles. */
    const headerSx: SxProps = {
        bgcolor: vars.logoBlue,
        justifyContent: 'left',
        flexDirection: 'row',
        alignItems: 'center',
        position: 'relative',
        textAlign: 'left',
        color: vars.white,
        width: '100%',
    };

    /** Controls wrapper element styles. */
    const controlsSx: SxProps = {
        justifyContent: 'right',
        flexDirection: 'row',
        alignItems: 'center',
    };

    /**
     * Toggles the visibility of the mobile menu.
     */
    const handleToggleMobileMenu = useCallback(
        () => setMobileMenuOpen(!mobileMenuOpen),
        [mobileMenuOpen],
    );

    /** Desktop and tablet view. */
    if (isDesktop || isTablet) {
        return (
            <Stack sx={{ ...headerSx }}>
                <Logo boxSx={{ pl: 3 }} />

                <Stack sx={{ flexGrow: 4 }}></Stack>

                <Stack sx={{ ...controlsSx }}>
                    <HeaderMenuButton
                        icon={mdiWeb}
                        text={i18n.language}
                        color={vars.white}
                        sx={{ textTransform: 'uppercase' }}
                    >
                        {[...i18n.languages]
                            .sort((a, b) => t(a).localeCompare(t(b)))
                            .map(x => (
                                <LanguageOption key={x} lang={x} />
                            ))}
                    </HeaderMenuButton>

                    <UserControl />
                </Stack>
            </Stack>
        );
    }

    /** Menu element styles. */
    const menuSx: SxProps = {
        position: 'absolute',
        bgcolor: vars.white,
        zIndex: 1220,
        bottom: 0,
        right: 0,
        top: 56,
        left: 0,
        p: 1,

        button: {
            textAlign: 'left',
            borderRadius: 1,
            width: '100%',
            fontSize: 16,
            height: 56,
            px: 3,

            '&.active': {
                bgcolor: vars.bgGreen,
            },
        },
    };

    return (
        <Stack direction='row'>
            <Stack sx={{ ...headerSx }}>
                <HeaderButton
                    title={t('menu')}
                    onClick={handleToggleMobileMenu}
                    icon={mdiMenu}
                    sx={{ bgColor: 'transparent', p: 2, color: vars.white, border: 0 }}
                />

                <Logo
                    boxSx={{
                        display: 'flex',
                        flex: 1,
                        justifyContent: 'left',
                        ml: isTablet ? 3 : 0,
                    }}
                />

                <Stack sx={{ ...controlsSx }}>
                    <UserControl />
                </Stack>
            </Stack>

            {isMobile && mobileMenuOpen && (
                <Stack sx={{ ...menuSx }}>
                    <HeaderButton text={t('projects')} />
                    <HeaderButton text={t('administration')} />
                    <HeaderButton text={t('profile')} sx={{ bgcolor: vars.bgGreen }} />

                    <Stack sx={{ button: { pl: 4.5 } }}>
                        <HeaderButton text={t('profile')} />
                        <HeaderButton text={t('passwordSecurity')} />
                        <HeaderButton text={t('accountSelection')} />
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
};
